import React, { useEffect, useState } from 'react';
import axios from 'axios';

const RatesTable = () => {
  const [ratesData, setRatesData] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]); // State for dynamic room types
  const [editRow, setEditRow] = useState(null); 
  const [addModalOpen, setAddModalOpen] = useState(false); 
  const [formData, setFormData] = useState({}); 

  // Fetch rates and room types when the component mounts
  useEffect(() => {
    fetchRates();
    fetchRoomTypes(); // Dynamic fetch for the dropdown
  }, []);

  // Function to fetch unique room types from the database
  const fetchRoomTypes = async () => {
    try {
      const response = await axios.get('https://admin.thepridehotel.in/api/inventory');
      // Extract unique room_type names from the response
      const uniqueTypes = [...new Set(response.data.map(item => item.room_type))];
      setRoomTypes(uniqueTypes);
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  };

  // Function to fetch rates from the backend
  const fetchRates = async () => {
    try {
      const response = await axios.get('https://admin.thepridehotel.in/api/inventory');
      const formattedData = response.data.map(item => ({
        id: item.id,
        roomType: item.room_type,
        deals: item.deals,
        cancellationPolicy: item.cancellationPolicy,
        dealPrice: item.dealPrice,
        rate: item.price,
        availability: item.available,
      }));
      setRatesData(formattedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load rates.');
    }
  };

  const handleEditClick = (item) => {
    setEditRow(item.id);
    setFormData({
      roomType: item.roomType,
      deals: item.deals,
      cancellationPolicy: item.cancellationPolicy,
      dealPrice: item.dealPrice === null ? '' : item.dealPrice,
      rate: item.rate,
      availability: item.availability,
    });
  };

  const handleAddClick = () => {
    setFormData({
      roomType: '',
      deals: '',
      cancellationPolicy: '',
      dealPrice: '',
      rate: '',
      availability: '',
    });
    setAddModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    const { roomType, deals, cancellationPolicy, dealPrice, rate, availability } = formData;

    if (!roomType || roomType.trim() === '') {
      alert('Room Type is required.');
      return;
    }
    const parsedRate = parseFloat(rate);
    if (isNaN(parsedRate) || parsedRate <= 0) {
      alert('Rate must be a positive number.');
      return;
    }

    try {
      await axios.put(`https://admin.thepridehotel.in/api/inventory/${editRow}`, {
        room_type: roomType,
        deals: deals,
        cancellationPolicy: cancellationPolicy,
        dealPrice: (dealPrice === '' || dealPrice === null) ? null : parseFloat(dealPrice),
        price: parsedRate,
        available: availability
      });
      setEditRow(null);
      fetchRates();
      alert('Rate updated successfully!');
    } catch (error) {
      console.error('Error updating rate:', error);
      alert('Failed to update rate.');
    }
  };

  const handleAdd = async () => {
    const { roomType, deals, cancellationPolicy, dealPrice, rate, availability } = formData;

    if (!roomType || roomType.trim() === '') {
      alert('Room Type is required.');
      return;
    }
    const parsedRate = parseFloat(rate);

    try {
      await axios.post(`https://admin.thepridehotel.in/api/inventory`, {
        room_type: roomType,
        deals: deals,
        cancellationPolicy: cancellationPolicy,
        dealPrice: (dealPrice === '' || dealPrice === null) ? null : parseFloat(dealPrice),
        price: parsedRate,
        available: availability,
      });
      setAddModalOpen(false);
      fetchRates();
      alert('Rate added successfully!');
    } catch (error) {
      console.error('Error adding rate:', error);
      alert('Failed to add rate.');
    }
  };

  const handleModalClose = () => {
    setEditRow(null);
    setAddModalOpen(false);
  };

  const styles = {
    container: {
      fontFamily: "'Open Sans', sans-serif",
      backgroundColor: '#fff',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
      padding: '30px',
      width: '100%',
      maxWidth: '2200px',
      boxSizing: 'border-box',
      margin: '0px auto',
      borderRadius: '12px'
    },
    headerTitle: { fontSize: '24px', color: '#111827', fontWeight: '600', margin: '0 0 20px' },
    topControlsRow: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '20px' },
    addRateBtn: {
      backgroundColor: '#AE7D54',
      color: 'white',
      padding: '10px 20px',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'background-color 0.2s ease',
    },
    filterIconBtn: {
      padding: '8px 14px',
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      backgroundColor: '#F9FAFB',
      color: '#4B5563',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      transition: 'background-color 0.2s ease, border-color 0.2s ease',
    },
    tableContainer: { overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px', fontSize: '14px' },
    tableHeader: { textAlign: 'left', padding: '16px 20px', color: '#6B7280', fontWeight: '600', borderBottom: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' },
    tableCell: { padding: '16px 20px', color: '#111827', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' },
    tableRowHover: { backgroundColor: '#F9FAFB' },
    rateCell: { fontWeight: '600', color: '#111827' },
    availabilityStatus: { padding: '5px 12px', borderRadius: '999px', fontWeight: '500', fontSize: '13px', display: 'inline-block' },
    availabilityRooms: { background: '#E0F2FE', color: '#3B82F6' },
    availabilityFull: { background: '#FFE4E6', color: '#EF4444' },
    ellipsisIcon: { color: '#9CA3AF', cursor: 'pointer', transition: 'color 0.2s ease', '&:hover': { color: '#4B5563' } },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    },
    modalContent: {
      background: '#fff',
      padding: '30px',
      borderRadius: '12px',
      width: '90%',
      maxWidth: '450px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      position: 'relative',
    },
    modalCloseButton: {
      position: 'absolute',
      top: '15px',
      right: '15px',
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#9CA3AF',
      lineHeight: 1,
    },
    modalLabel: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    modalInput: {
      width: '100%',
      padding: '10px 14px',
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
      marginBottom: '16px'
    },
    modalSelect: {
      width: '100%',
      padding: '10px 14px',
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box',
      backgroundColor: '#fff',
      cursor: 'pointer',
      marginBottom: '16px'
    },
    modalButtonsContainer: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
    modalCancelBtn: {
      padding: '10px 20px',
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      backgroundColor: '#fff',
      color: '#374151',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    modalSaveBtn: {
      padding: '10px 20px',
      backgroundColor: '#AE7D54',
      color: '#fff',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    }
  };

  const getAvailabilityStyle = (text) => {
    const availabilityText = String(text).toLowerCase();
    if (availabilityText.includes('full')) return styles.availabilityFull;
    if (parseInt(availabilityText) > 0 || availabilityText.includes('rooms')) return styles.availabilityRooms;
    return {};
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.headerTitle}>Rates & Inventory</h1>
      <div style={styles.topControlsRow}>
        <button style={styles.addRateBtn} onClick={handleAddClick}>
          Add rate
        </button>
        <button style={styles.filterIconBtn}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ marginRight: 5 }}>
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>
          Filter
        </button>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Room type</th>
              <th style={styles.tableHeader}>Deals</th>
              <th style={styles.tableHeader}>Cancellation policy</th>
              <th style={styles.tableHeader}>Deal price</th>
              <th style={styles.tableHeader}>Rate</th>
              <th style={styles.tableHeader}>Availability</th>
              <th style={styles.tableHeader}></th>
            </tr>
          </thead>
          <tbody>
            {ratesData.map((item, index) => (
              <tr key={item.id}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor)}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <td style={styles.tableCell}>{item.roomType}</td>
                <td style={styles.tableCell}>{item.deals}</td>
                <td style={styles.tableCell}>{item.cancellationPolicy}</td>
                <td style={styles.tableCell}>₹ {item.dealPrice}</td>
                <td style={{ ...styles.tableCell, ...styles.rateCell }}>₹ {item.rate}</td>
                <td style={styles.tableCell}>
                  <span style={{ ...styles.availabilityStatus, ...getAvailabilityStyle(item.availability) }}>
                    {item.availability === 'Full' ? 'Full' : `${item.availability} rooms`}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <svg style={styles.ellipsisIcon} onClick={() => handleEditClick(item)}
                    xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="19" r="1"></circle>
                  </svg>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(editRow || addModalOpen) && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <button style={styles.modalCloseButton} onClick={handleModalClose}>&times;</button>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              {editRow ? 'Edit Rate' : 'Add Rate'}
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.modalLabel}>Room Type</label>
              <select
                name="roomType"
                value={formData.roomType || ''}
                onChange={handleInputChange}
                style={styles.modalSelect}
                disabled={!!editRow} // Usually room types aren't changed on existing inventory rows
              >
                <option value="">Select Room Type</option>
                {roomTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {[
              { name: 'deals', label: 'Deals' },
              { name: 'cancellationPolicy', label: 'Cancellation Policy' },
              { name: 'dealPrice', label: 'Deal Price', type: 'number' },
              { name: 'rate', label: 'Rate', type: 'number' },
              { name: 'availability', label: 'Availability' },
            ].map((field) => (
              <div key={field.name}>
                <label style={styles.modalLabel}>{field.label}</label>
                <input
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleInputChange}
                  type={field.type || 'text'}
                  style={styles.modalInput}
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                />
              </div>
            ))}

            <div style={styles.modalButtonsContainer}>
              <button onClick={handleModalClose} style={styles.modalCancelBtn}>Cancel</button>
              <button onClick={editRow ? handleUpdate : handleAdd} style={styles.modalSaveBtn}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RatesTable;